import { UserPresenter } from '@shared/modules/users/user.presenter';
import { Presenter } from '@shared/presenters/presenter';
import { TIME_FORMAT } from '@shared/utils/date-time';
import type { LatLng } from '@shared/utils/geo';
import { distanceBetween, readableFromMeters } from '@shared/utils/geo';
import type { Duration } from 'luxon';
import { DateTime } from 'luxon';
import { BookingPresenter } from '../bookings/booking.presenter';
import { CarerPresenter } from '../carers/carer.presenter';
import { CheckOutPresenter } from '../check-outs';
import { ClientPresenter } from '../clients/client.presenter';

export class CheckInPresenter extends Presenter<any> {

    get booking(): BookingPresenter {
        return this.present('booking.data', BookingPresenter);
    }

    get manualBy(): UserPresenter {
        return this.present('manual_by.data', UserPresenter);
    }

    get client() {
        return this.present('client.data', ClientPresenter);
    }

    get carer(): CarerPresenter {
        return this.present('carer.data', CarerPresenter);
    }

    get visited() {
        return this.data.hasBeenVisited;
    }

    get whenDateTime(): DateTime {
        return this._get('when', () => DateTime.fromISO(this.data.when));
    }

    get whenTime() {
        return this.whenDateTime.toFormat(TIME_FORMAT);
    }

    get isToday() {
        return this.whenDateTime.day === DateTime.local().day;
    }

    get whenDateReadable() {
        return this._get('whenDateReadable', () => this.formatDateReadable(this.whenDateTime));
    }

    get duration(): string {
        return this._get('duration', () => this.formatDuration(this.durationSeconds));
    }

    get durationSeconds(): number {
        return this._get('durationSeconds', () => {
            const checkOut = this.checkOut;

            if (checkOut) {
                return checkOut.whenDateTime.diff(this.whenDateTime, 'seconds').seconds;
            }

            return null;
        });
    }

    get punctuality(): Duration {
        return this._get('punctuality', () => {
            const start = this.booking?.start;

            return start ? this.whenDateTime.diff(start, 'minutes') : null;
        });
    }

    get punctualityReadable() {
        return this._get('punctualityReadable', () => {
            const punctuality = this.punctuality;

            if (!punctuality) {
                return null;
            }

            const minutes = Math.round(Math.abs(punctuality.minutes));

            if (minutes === 0) {
                return 'On time';
            }

            return `${minutes}m ${punctuality.minutes < 0 ? 'early' : 'late'}`;
        });
    }

    get metresFromBookingAddress() {
        return this._get('metresFrom', () => {
            const checkInLatLng = { lat: this.data.lat, lng: this.data.lng };
            const bookingLatLng = this.booking?.address?.googleLatLng;

            return bookingLatLng ? distanceBetween(checkInLatLng, bookingLatLng) : null;
        });
    }

    get googleLatLng(): LatLng | null {
        return this._get('googleLatLng', (): LatLng | null => {
            if (this.data.lat && this.data.lng) {
                return { lat: this.data.lat, lng: this.data.lng };
            }

            return null;
        });
    }

    get locationAccuracy() {
        return this._get('locationAccuracy', () => this.data.location_accuracy);
    }

    get locationAccuracyReadable() {
        return this._get('locationAccuracyReadable', () => readableFromMeters(this.data.location_accuracy));
    }

    get locationStaleness(): number {
        return this._get('locationStaleness', () => {
            if (!this.data.location_acquired_at) {
                return;
            }

            const acquired = this.toDateTime('location_acquired_at', this.data.location_acquired_at);

            return this.whenDateTime.diff(acquired, 'seconds').seconds;
        });
    }

    get checkOut(): CheckOutPresenter {
        return this.present('check_out.data', CheckOutPresenter);
    }

    durationDiff(expectedDuration: number, absolute: boolean = false): number {
        return this._get(`durationDiff:${expectedDuration}:${absolute}`, () => {
            const actualDuration = this.durationSeconds;

            if (!actualDuration) {
                return;
            }

            const diff = actualDuration - expectedDuration;

            return absolute ? Math.abs(diff) : diff;
        });
    }

    formatDurationOverrun(expectedDuration: number): string {
        return this._get(`formatDurationOverrun:${expectedDuration}`, () => {
            const diff = this.durationDiff(expectedDuration);

            if (diff === 0) {
                return;
            }

            return `${this.formatDurationShort(Math.abs(diff))} ${diff < 0 ? 'under' : 'over'}`;
        });
    }

    formatPunctuality(expectedStart: DateTime): string {
        return this._get(`formatPunctuality:${expectedStart.valueOf()}`, () => {
            const minutes = this.punctualityMinutes(expectedStart);

            if (minutes === 0) {
                return;
            }

            return `${Math.abs(minutes)}m ` + (minutes < 0 ? 'early' : 'late');
        });
    }


    punctualityMinutes(expectedStart: DateTime, absolute: boolean = false): number {
        return this._get(`punctualityMinutes:${expectedStart.valueOf()}:${absolute}`, () => {
            const diff = Math.round(this.whenDateTime.diff(expectedStart, 'minutes').minutes);

            return absolute ? Math.abs(diff) : diff;
        });
    }
}
